import type { ChatSessionSummary } from '../../contexts/ChatContext';
import type { SuggestedPrompt } from './SuggestedPrompts';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import HistoryIcon from '@mui/icons-material/History';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatSessionHistory } from './ChatSessionHistory';
import { SuggestedPrompts } from './SuggestedPrompts';

export interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  isLoading?: boolean;
}

interface ChatWindowProps {
  open: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  title?: string;
  isLoading?: boolean;
  onSendMessage?: (message: string) => void;
  showInput?: boolean;
  suggestedPrompts?: SuggestedPrompt[];
  onSuggestedPromptClick?: (prompt: SuggestedPrompt) => void;
  contextInfo?: Record<string, number>;
  sessions?: ChatSessionSummary[];
  sessionsLoading?: boolean;
  activeThreadId?: string | null;
  showSessionControls?: boolean;
  onLoadSessions?: () => void;
  onSelectSession?: (session: ChatSessionSummary) => void;
  onNewSession?: () => void;
  onDeleteSession?: (threadId: string) => void;
}

/**
 * Custom link renderer for ReactMarkdown that uses React Router for internal links.
 * This allows clickable review links to navigate within the SPA.
 * All links open in new tabs to preserve chat context.
 */
function MarkdownLink({
  href,
  children,
}: {
  href?: string;
  children?: React.ReactNode;
}) {
  // All links open in new tabs to preserve the chat context
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#1976d2',
        textDecoration: 'underline',
      }}
    >
      {children}
    </a>
  );
}

/**
 * A reusable chat window component that displays in the bottom-right corner.
 * Designed to be modular for future AI chat interactions.
 */
function ChatWindow({
  open,
  onClose,
  messages,
  title = 'AI Assistant',
  isLoading = false,
  onSendMessage,
  showInput = true,
  suggestedPrompts = [],
  onSuggestedPromptClick,
  contextInfo,
  sessions = [],
  sessionsLoading = false,
  activeThreadId = null,
  showSessionControls = false,
  onLoadSessions,
  onSelectSession,
  onNewSession,
  onDeleteSession,
}: ChatWindowProps) {
  const [_copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when messages change or loading state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const _handleCopyMessage = async (message: ChatMessage) => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopiedMessageId(message.id);
      setTimeout(() => setCopiedMessageId(null), 2000);
    }
    catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading || !onSendMessage)
      return;

    Sentry.setTag('ai_chat', true);

    onSendMessage(inputValue.trim());
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        className="chat-window"
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: isExpanded ? 1000 : 400,
          maxWidth: 'calc(100vw - 48px)',
          height: isExpanded ? '80%' : 500,
          maxHeight: isExpanded ? '90%' : 500,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          zIndex: 1300,
          transition: 'width 0.3s ease, max-height 0.3s ease',
        }}
      >
        {/* Header */}
        <Box
          className="chat-window-header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            {contextInfo && Object.keys(contextInfo).length > 0 && (
              <Tooltip
                title={(
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                      What Context Does Chat Have?
                    </Typography>
                    {Object.entries(contextInfo).map(([key, value], index, array) => (
                      <Typography
                        key={key}
                        variant="body2"
                        sx={{
                          mb: index < array.length - 1 ? 0.5 : 0,
                          color: 'text.primary',
                        }}
                      >
                        {key}
                        :
                        {value}
                      </Typography>
                    ))}
                  </Box>
                )}
                arrow
                placement="bottom"
              >
                <Box
                  sx={{
                    'cursor': 'help',
                    'display': 'flex',
                    'alignItems': 'center',
                    'fontSize': '1.25rem',
                    'opacity': 0.9,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  🧠
                </Box>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {showSessionControls && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    onLoadSessions?.();
                    setHistoryAnchorEl(e.currentTarget);
                  }}
                  sx={{ 'color': 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                  title="Conversation history"
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onNewSession?.()}
                  sx={{ 'color': 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                  title="New conversation"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </>
            )}
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{ 'color': 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ 'color': 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {showSessionControls && (
          <ChatSessionHistory
            anchorEl={historyAnchorEl}
            open={Boolean(historyAnchorEl)}
            onClose={() => setHistoryAnchorEl(null)}
            sessions={sessions}
            loading={sessionsLoading}
            activeThreadId={activeThreadId}
            onSelect={threadId => onSelectSession?.(threadId)}
            onDelete={threadId => onDeleteSession?.(threadId)}
          />
        )}

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            bgcolor: '#fafafa',
            minHeight: 200,
          }}
        >
          {messages.length === 0 && !isLoading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">No messages yet</Typography>
            </Box>
          )}

          {messages.map(message => (
            <Box
              className="chat-message-container"
              key={message.id}
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Paper
                className="chat-message-bubble"
                elevation={1}
                sx={{
                  px: 2,
                  py: 1.5,
                  maxWidth: '90%',
                  bgcolor: message.role === 'user' ? 'primary.main' : 'white',
                  color: message.role === 'user' ? 'white' : 'text.primary',
                  borderRadius: 2,
                  position: 'relative',
                }}
              >
                {message.isLoading
                  ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} color="inherit" />
                        <Typography variant="body2">Generating...</Typography>
                      </Box>
                    )
                  : (
                      <>
                        <Box
                          sx={{
                            'lineHeight': 1.5,
                            '& p': { fontSize: '0.95rem', mb: 0.75 },
                            '& p:last-child': { mb: 0 },
                            '& ul, & ol': { pl: 2, my: 0.75 },
                            '& li': { mb: 0.25 },
                            '& a': { color: 'primary.main', textDecoration: 'underline' },
                          }}
                        >
                          <ReactMarkdown components={{ a: MarkdownLink }}>{message.content}</ReactMarkdown>
                        </Box>
                      </>
                    )}
              </Paper>
              <Typography
                variant="caption"
                sx={{ mt: 0.5, color: 'text.secondary' }}
              >
                {message.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            </Box>
          ))}

          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: 'white',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Thinking...
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Suggested Prompts */}
          {suggestedPrompts.length > 0 && onSuggestedPromptClick && !isLoading && (
            <SuggestedPrompts
              prompts={suggestedPrompts}
              onPromptClick={onSuggestedPromptClick}
              disabled={isLoading}
            />
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        {showInput && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'white',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                placeholder="Type a message..."
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                sx={{
                  'bgcolor': 'primary.main',
                  'color': 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>
    </Slide>
  );
}

export default ChatWindow;
